export const ROLE_OPTIONS = [
  'Founder / CEO / COO',
  'Chief Customer Officer',
  'Head / VP / Director of Customer Success',
  'Customer Success Team Lead',
  'Customer Success Manager',
  'Account Manager',
  'Customer Success Operations',
  'Revenue Operations',
  'Other',
] as const;

export const ACCOUNT_COUNT_OPTIONS = [
  'Fewer than 100',
  '100-199',
  '200-499',
  '500-999',
  '1,000-1,999',
  '2,000+',
] as const;

export const TEAM_SIZE_OPTIONS = ['1', '2-3', '4-6', '7-10', '11+'] as const;

export const ACV_OPTIONS = [
  'Under $2,500',
  '$2,500-$4,999',
  '$5,000-$9,999',
  '$10,000-$24,999',
  '$25,000+',
  "I'm not sure",
] as const;

export const RISK_PROCESS_OPTIONS = [
  'CSM judgment / manual review',
  'CRM or customer health score',
  'Customer Success platform',
  'Spreadsheet',
  'Product usage / analytics',
  'BI or internally built risk model',
  'Regular churn-risk meetings',
  'We usually find out when the customer tells us',
  "We don't have a consistent process",
  'Other',
] as const;

export const IMPROVEMENT_OPTIONS = [
  'Spot customers at risk earlier',
  'Know which customers actually need attention',
  'Reduce false alarms and unnecessary reviews',
  'Understand why a customer is at risk',
  'Know what action to take',
  'Keep on top of churn risk without adding more CS people',
  'Lose less recurring revenue to preventable churn',
  'Our current process works well enough',
  'Other',
] as const;

export const FREQUENCY_OPTIONS = ['Often', 'Sometimes', 'Rarely', 'Never', "I'm not sure"] as const;

export const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP'] as const;

export type WaitlistAnswers = {
  name: string;
  email: string;
  company: string;
  role: string;
  roleOther: string;
  accountCount: string;
  teamSize: string;
  acv: string;
  riskProcesses: string[];
  riskProcessOther: string;
  frustration: string;
  improvement: string;
  improvementOther: string;
  churnSignsFrequency: string;
  couldActFrequency: string;
  bargainCurrency: string;
  bargainAmount: string;
  expensiveCurrency: string;
  expensiveAmount: string;
  anythingElse: string;
};

export const initialWaitlistAnswers: WaitlistAnswers = {
  name: '',
  email: '',
  company: '',
  role: '',
  roleOther: '',
  accountCount: '',
  teamSize: '',
  acv: '',
  riskProcesses: [],
  riskProcessOther: '',
  frustration: '',
  improvement: '',
  improvementOther: '',
  churnSignsFrequency: '',
  couldActFrequency: '',
  bargainCurrency: 'USD',
  bargainAmount: '',
  expensiveCurrency: 'USD',
  expensiveAmount: '',
  anythingElse: '',
};

export function toWaitlistLeadFields(answers: WaitlistAnswers) {
  const role = answers.role === 'Other' ? `Other: ${answers.roleOther}` : answers.role;
  const risk = answers.riskProcesses
    .map((item) => (item === 'Other' ? `Other: ${answers.riskProcessOther}` : item))
    .join('; ');
  const improvement =
    answers.improvement === 'Other' ? `Other: ${answers.improvementOther}` : answers.improvement;

  return {
    company: answers.company.trim(),
    role,
    accountCount: answers.accountCount,
    teamSize: answers.teamSize,
    acv: answers.acv,
    riskProcess: risk,
    frustration: answers.frustration.trim(),
    improvement,
    churnSignsFrequency: answers.churnSignsFrequency,
    couldActFrequency: answers.couldActFrequency,
    bargainPrice: `${answers.bargainCurrency} ${answers.bargainAmount}`.trim(),
    expensivePrice: `${answers.expensiveCurrency} ${answers.expensiveAmount}`.trim(),
    anythingElse: answers.anythingElse.trim(),
  };
}

export function formatWaitlistAnswers(answers: WaitlistAnswers): string {
  const role = answers.role === 'Other' ? `Other: ${answers.roleOther}` : answers.role;
  const risk = answers.riskProcesses
    .map((item) => (item === 'Other' ? `Other: ${answers.riskProcessOther}` : item))
    .join('; ');
  const improvement =
    answers.improvement === 'Other' ? `Other: ${answers.improvementOther}` : answers.improvement;

  return [
    `Company: ${answers.company}`,
    `Role: ${role}`,
    `Paying accounts: ${answers.accountCount}`,
    `People managing customers: ${answers.teamSize}`,
    `Typical ACV: ${answers.acv}`,
    `How identify risk: ${risk}`,
    `Biggest frustration: ${answers.frustration}`,
    `Most valuable improvement: ${improvement}`,
    `Signs before churn: ${answers.churnSignsFrequency}`,
    `Could have acted earlier: ${answers.couldActFrequency}`,
    `Bargain monthly price: ${answers.bargainCurrency} ${answers.bargainAmount}`,
    `Too expensive monthly price: ${answers.expensiveCurrency} ${answers.expensiveAmount}`,
    answers.anythingElse ? `Anything else: ${answers.anythingElse}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

export function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

/** Major consumer / workplace email providers we explicitly accept. */
const MAJOR_EMAIL_PROVIDERS = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'outlook.co.uk',
  'hotmail.com',
  'hotmail.co.uk',
  'live.com',
  'msn.com',
  'yahoo.com',
  'yahoo.co.uk',
  'ymail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'zoho.com',
  'zohomail.com',
  'gmx.com',
  'gmx.net',
  'mail.com',
  'fastmail.com',
  'fastmail.fm',
  'hey.com',
  'pm.me',
]);

/** Disposable / throwaway domains we reject. */
const DISPOSABLE_EMAIL_DOMAINS = new Set([
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamail.net',
  'sharklasers.com',
  'grr.la',
  'guerrillamailblock.com',
  '10minutemail.com',
  '10minutemail.net',
  'tempmail.com',
  'temp-mail.org',
  'temp-mail.io',
  'throwawaymail.com',
  'yopmail.com',
  'yopmail.fr',
  'trashmail.com',
  'trashmail.me',
  'getnada.com',
  'nada.email',
  'maildrop.cc',
  'discard.email',
  'mailnesia.com',
  'fakeinbox.com',
  'tempail.com',
  'emailondeck.com',
  'mintemail.com',
  'moakt.com',
  'spamgourmet.com',
  'mailcatch.com',
  'mailnull.com',
  'spamspot.com',
  'tempr.email',
  'dispostable.com',
  'mailinator.net',
  'inboxkitten.com',
  'getairmail.com',
  'mytemp.email',
  'tmpmail.org',
  'tmpmail.net',
  'emailfake.com',
  'crazymailing.com',
]);

const RESERVED_EMAIL_DOMAINS = new Set([
  'example.com',
  'example.org',
  'example.net',
  'example.edu',
  'test.com',
  'localhost',
  'invalid',
  'local',
]);

const EMAIL_PATTERN =
  /^[a-z0-9](?:[a-z0-9_'.+-]*[a-z0-9_+-])?@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;

export type WaitlistEmailValidation = { ok: true } | { ok: false; message: string };

function isLikelyCompanyDomain(domain: string): boolean {
  const labels = domain.split('.');
  if (labels.length < 2) {
    return false;
  }

  const tld = labels[labels.length - 1];
  if (!/^[a-z]{2,24}$/i.test(tld)) {
    return false;
  }

  // Reject domains that are only a disposable-style single-word + obscure TLD with digits everywhere.
  if (/^\d+$/.test(labels[0])) {
    return false;
  }

  // Company domains should look like brand.tld or brand.co.uk — not bare IPs or localhost-style hosts.
  if (labels.some((label) => label.length === 0 || label.startsWith('-') || label.endsWith('-'))) {
    return false;
  }

  return true;
}

/**
 * Accepts major providers (Gmail, Outlook, Yahoo, …) and legitimate company domains.
 * Rejects malformed, reserved, and disposable addresses.
 */
export function validateWaitlistEmail(email: string): WaitlistEmailValidation {
  const trimmed = email.trim().toLowerCase();

  if (!trimmed) {
    return { ok: false, message: 'Enter your work email.' };
  }

  if (trimmed.length > 254 || trimmed.includes('..') || trimmed.startsWith('.') || trimmed.includes('@.')) {
    return { ok: false, message: 'Enter a valid email address.' };
  }

  if (!EMAIL_PATTERN.test(trimmed)) {
    return { ok: false, message: 'Enter a valid email address.' };
  }

  const atIndex = trimmed.lastIndexOf('@');
  const domain = trimmed.slice(atIndex + 1);

  if (RESERVED_EMAIL_DOMAINS.has(domain) || DISPOSABLE_EMAIL_DOMAINS.has(domain)) {
    return {
      ok: false,
      message: 'Please use a company email or a major provider like Gmail, Outlook, or Yahoo.',
    };
  }

  // Also catch disposable subdomains e.g. foo.mailinator.com
  const domainLabels = domain.split('.');
  for (let i = 0; i < domainLabels.length - 1; i += 1) {
    const suffix = domainLabels.slice(i).join('.');
    if (DISPOSABLE_EMAIL_DOMAINS.has(suffix)) {
      return {
        ok: false,
        message: 'Please use a company email or a major provider like Gmail, Outlook, or Yahoo.',
      };
    }
  }

  if (MAJOR_EMAIL_PROVIDERS.has(domain)) {
    return { ok: true };
  }

  if (!isLikelyCompanyDomain(domain)) {
    return {
      ok: false,
      message: 'Please use a company email or a major provider like Gmail, Outlook, or Yahoo.',
    };
  }

  return { ok: true };
}

export function isAcceptedWaitlistEmail(email: string): boolean {
  return validateWaitlistEmail(email).ok;
}
